import {Disclosure} from '@github-ui/disclosure'
import {AnchoredOverlay, Heading, Tooltip, defaultPaneWidth, useMergedRefs} from '@primer/react'
import {FeatureFlags} from '@primer/react/experimental'
import type {OverlayProps} from '@primer/react'
import {clsx} from 'clsx'
import {useId, useLayoutEffect, useMemo, useRef, useState} from 'react'
import type {MouseEvent, ReactNode, Ref} from 'react'
import {CollapsibleSidebarContext, useCollapsibleSidebarContext} from '../CollapsibleSidebar/CollapsibleSidebar'
import {GroupedContext} from './sidebar-context'
import type {ChildrenProps, Icon} from './sidebar-context'
import {SidebarAction} from './Action'
import actionStyles from './Action.module.css'
import {DisclosurePanel} from './DisclosurePanel'
import rowStyles from './Row.module.css'
import styles from './SectionGroup.module.css'
import {containsCurrent, sidebarChildKind} from './contains-current'

export interface SectionGroupProps extends ChildrenProps {
  title: string
  icon: Icon
  action?: ReactNode | ((props: {close: () => void}) => ReactNode)
  actionVisibility?: 'always' | 'hover'
  search?: ReactNode
  variant?: 'default' | 'collapsible'
  defaultOpen?: boolean
  headingLevel?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
  /** Class applied to the collapsed popover's content wrapper. */
  popoverClassName?: string
  /** Primer overlay width; defaults to the expanded sidebar's width. */
  popoverWidth?: OverlayProps['width']
  popoverMaxWidth?: OverlayProps['maxWidth']
  /** Primer overlay height limit; defaults to 'large'. */
  popoverMaxHeight?: OverlayProps['maxHeight']
  ref?: Ref<HTMLButtonElement>
}

function GroupContents({children, search}: Pick<SectionGroupProps, 'children' | 'search'>) {
  const context = useCollapsibleSidebarContext()
  const expandedContext = useMemo(
    () => ({...context, isExpanded: true, visualIsExpanded: true, isExpansionSettled: true}),
    [context],
  )

  return (
    <CollapsibleSidebarContext value={expandedContext}>
      <GroupedContext value>
        {search}
        <div className={styles.Items}>{children}</div>
      </GroupedContext>
    </CollapsibleSidebarContext>
  )
}

type SectionGroupHeaderProps = Pick<
  SectionGroupProps,
  'title' | 'icon' | 'action' | 'actionVisibility' | 'variant' | 'headingLevel' | 'ref'
> & {showCurrentIndicator: boolean}

function SectionGroupHeader({
  title,
  icon: GroupIcon,
  action,
  actionVisibility = 'always',
  variant = 'default',
  headingLevel = 'h3',
  showCurrentIndicator,
  ref,
}: SectionGroupHeaderProps) {
  const {isExpanded} = useCollapsibleSidebarContext()
  const isCollapsible = variant === 'collapsible'

  return (
    <div className={clsx(styles.GroupHeader, actionStyles.actionRow, isCollapsible && actionStyles.disclosureRow)}>
      {isCollapsible ? (
        <Heading as={headingLevel} className={styles.Heading}>
          <Disclosure.Trigger
            ref={isExpanded ? ref : undefined}
            className={clsx(rowStyles.Row, showCurrentIndicator && rowStyles.current)}
          >
            <GroupIcon aria-hidden className={rowStyles.Icon} />
            <span className={rowStyles.Label} data-sidebar-collapse-content>
              {title}
            </span>
            {action ? <span aria-hidden className={actionStyles.ActionSpace} /> : null}
            <span className={styles.Chevron} data-sidebar-collapse-content>
              <Disclosure.Chevron />
            </span>
          </Disclosure.Trigger>
        </Heading>
      ) : (
        <Heading as={headingLevel} className={clsx(styles.Heading, rowStyles.RowLayout, styles.GroupTitle)}>
          <GroupIcon aria-hidden className={rowStyles.Icon} />
          <span data-sidebar-collapse-content>{title}</span>
        </Heading>
      )}
      {action ? (
        <SidebarAction hover={actionVisibility === 'hover'}>
          {typeof action === 'function' ? action({close: () => {}}) : action}
        </SidebarAction>
      ) : null}
    </div>
  )
}

function Popover({
  title,
  icon: GroupIcon,
  action,
  search,
  headingLevel = 'h3',
  popoverClassName,
  popoverWidth,
  popoverMaxWidth,
  popoverMaxHeight = 'large',
  children,
  ref,
}: SectionGroupProps) {
  const {expandedSidebarWidth = defaultPaneWidth.small} = useCollapsibleSidebarContext()
  const [popoverState, setPopoverState] = useState<'closed' | 'open' | 'dismissed'>('closed')
  const open = popoverState === 'open'
  const labelId = useId()
  const anchorRef = useRef<HTMLButtonElement>(null)
  const mergedRef = useMergedRefs(ref, anchorRef)
  // Primer captures returnFocusRef when opening. Leave it empty and restore
  // explicitly only on dismissal, never after a navigation activation.
  const returnFocusRef = useRef<HTMLElement>(null)
  useLayoutEffect(() => {
    if (popoverState === 'dismissed') {
      anchorRef.current?.focus()
    }
  }, [popoverState])
  const dismissOnActivation = (event: MouseEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement
    const control = target.closest('a[href]')
    if (
      !control ||
      !event.currentTarget.contains(control) ||
      target.closest('input, textarea, select, [contenteditable], [data-sidebar-search], [data-sidebar-action]') ||
      control.hasAttribute('aria-expanded')
    )
      return
    // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
    setPopoverState('closed')
  }
  return (
    <AnchoredOverlay
      open={open}
      onOpen={() => setPopoverState('open')}
      onClose={gesture => {
        setPopoverState(gesture === 'click-outside' ? 'closed' : 'dismissed')
      }}
      side="outside-right"
      align="start"
      anchorRef={anchorRef}
      focusZoneSettings={{disabled: true}}
      overlayProps={{
        role: 'dialog',
        'aria-labelledby': labelId,
        width: popoverWidth,
        maxWidth: popoverMaxWidth,
        maxHeight: popoverMaxHeight,
        overflow: 'auto',
        returnFocusRef,
        style:
          popoverWidth === undefined
            ? {inlineSize: `min(${expandedSidebarWidth}px, calc(100vw - var(--base-size-32)))`}
            : undefined,
      }}
      renderAnchor={anchorProps => (
        <FeatureFlags flags={{primer_react_merged_forwarded_refs: true}}>
          <Tooltip
            text={open ? '' : title}
            direction="e"
            delay="medium"
            _privateDisableTooltip={open}
            popover={open ? undefined : 'auto'}
          >
            <button
              {...anchorProps}
              data-component="Sidebar.SectionGroup.Trigger"
              ref={mergedRef}
              type="button"
              className={clsx(rowStyles.Row, containsCurrent(children) && rowStyles.current)}
              aria-label={title}
              aria-haspopup="dialog"
            >
              <GroupIcon aria-hidden className={rowStyles.Icon} />
            </button>
          </Tooltip>
        </FeatureFlags>
      )}
    >
      {/* Native descendants emit click for keyboard activation too. This container only delegates dismissal. */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div
        className={clsx(styles.Popover, popoverClassName)}
        onClick={dismissOnActivation}
        data-component="Sidebar.SectionGroup.Popover"
      >
        <GroupContents
          search={
            <div className={clsx(styles.PopoverHeader, search && styles.withSearch)}>
              <div className={clsx(styles.GroupHeader, actionStyles.actionRow)}>
                <Heading as={headingLevel} id={labelId} className={styles.Heading}>
                  {title}
                </Heading>
                {action ? (
                  <SidebarAction>
                    {typeof action === 'function' ? action({close: () => setPopoverState('dismissed')}) : action}
                  </SidebarAction>
                ) : null}
              </div>
              {search ? <div data-sidebar-search>{search}</div> : null}
            </div>
          }
        >
          {children}
        </GroupContents>
      </div>
    </AnchoredOverlay>
  )
}

export const SectionGroup = Object.assign(
  function SidebarSectionGroup(props: SectionGroupProps) {
    const {children, search, variant = 'default', defaultOpen} = props
    const {isExpanded, visualIsExpanded} = useCollapsibleSidebarContext()
    const hasCurrent = containsCurrent(children)
    const [isDisclosureOpen, setIsDisclosureOpen] = useState(() => defaultOpen ?? hasCurrent)
    const showRail = !visualIsExpanded
    const showGroupContents = isExpanded && (variant === 'default' || isDisclosureOpen)

    return (
      <>
        {showRail ? <Popover {...props} /> : null}
        <Disclosure open={showGroupContents} onOpenChange={setIsDisclosureOpen}>
          <div
            className={styles.Group}
            hidden={showRail}
            inert={!isExpanded || undefined}
            data-component="Sidebar.SectionGroup"
          >
            <SectionGroupHeader {...props} showCurrentIndicator={!isDisclosureOpen && hasCurrent} />
            <DisclosurePanel>
              <GroupContents search={search}>{children}</GroupContents>
            </DisclosurePanel>
          </div>
        </Disclosure>
      </>
    )
  },
  {[sidebarChildKind]: 'container' as const},
)
